// src/achievementsAdmin/index.js — 🏆 the HO Achievements tab (v126.3, PR4).
//
// Four jobs: the trophy GRID (every store's + person's badges, filterable),
// the Award-a-⭐ form (the human touch — AM own area / HO), the ANNOUNCE
// switch (default OFF, safe-auto-content rule) with its preview of what would
// blast, and the one-time history backfill button.
// Artwork comes from the shared Achievements bundle when loaded (both apps
// load dist/achievements.js); falls back to a plain emoji chip.
//
// v126.17 (Matt: "why in admin can I not see anything allude to the new badge
// system… what badges get given out for what?") — the tab now leads with a
// HOW-IT-WORKS card + a full catalogue. The catalogue is IMPORTED from the
// award engine itself (src/worker/achievements.js has no worker deps), so
// what admin shows is exactly what the sweep awards — it can never drift.

import { BADGES } from '../worker/achievements.js';

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}
function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function art(a, size) {
  try { if (typeof Achievements !== 'undefined' && Achievements.awardSvg) return Achievements.awardSvg(a, size); } catch (e) {}
  return '<div style="width:' + size + 'px;height:' + size + 'px;border-radius:50%;background:var(--elc-green-soft);display:flex;align-items:center;justify-content:center;font-size:' + Math.round(size / 2) + 'px">' + (a.emoji || '🏅') + '</div>';
}

var _awards = null;
var _filter = '';
var _announce = null;
var _showCat = false;

var FREQ_LABELS = { week: 'Weekly', month: 'Monthly', quarter: 'Quarterly', year: 'Yearly', ongoing: 'As earned', adhoc: 'Any time' };

function howItWorksHtml() {
  return '<div style="background:var(--elc-green-soft);border:1.5px solid var(--elc-green,#009E3D);border-radius:14px;padding:14px;margin-bottom:14px">' +
    '<div style="font-weight:900;font-size:14.5px;margin-bottom:6px">🏆 How the badge system works</div>' +
    '<div style="font-size:12.5px;line-height:1.65;color:var(--adm-comms-body)">' +
      '<b>Automatic badges</b> are computed <b>overnight</b> from real results — sales uploads, reviews, SVRs, training, attendance. Nobody enters anything, and competitive badges use <b>units per 1,000 AED</b> so small stores compete fairly. Top 3 get 🥇🥈🥉.<br>' +
      '<b>⭐ Star badges</b> are the human touch: <b>Head Office can award anyone; Area Managers award within their own area</b> — tap <b>⭐ Award a star</b> above, pick a store or person, give a reason. Max <b>3 per week</b> each so they stay special; the badge spotlights for <b>7 days</b> with your reason on it.<br>' +
      '<b>📣 Announcements</b> below is the megaphone: OFF = badges appear quietly in the app; ON = every NEW badge posts to 📰 News + the winning store&rsquo;s 💬 chat + a push.<br>' +
      '<b>⏪ Back-compute history</b> fills in every badge earned since January — run it once per environment.<br>' +
      'Staff see badges on the <b>KPI → 🏆 Badges</b> tab, a clock-in splash for new ones, and their own on <b>My Development</b> and quarterly reviews.' +
    '</div></div>';
}

function catalogueHtml() {
  var keys = Object.keys(BADGES);
  var storeRows = [], staffRows = [];
  keys.forEach(function (k) {
    var b = BADGES[k];
    var row = '<tr>' +
      '<td style="padding:7px 10px;white-space:nowrap"><span style="font-size:17px">' + b.emoji + '</span> <b style="font-size:12.5px">' + esc(b.name) + '</b></td>' +
      // v135.2: each frequency is its own span, so the Arabic layer can swap each word.
      '<td style="padding:7px 10px;font-size:11.5px;color:var(--sub,#5e6863);white-space:nowrap">' + b.freqs.map(function (f) { return '<span>' + (FREQ_LABELS[f] || f) + '</span>'; }).join(' · ') + '</td>' +
      '<td style="padding:7px 10px;font-size:12px">' + esc(b.metric) + (b.competitive ? ' <span style="color:var(--ui-warn);font-size:11px;font-weight:700">🏁 top store wins</span>' : '') + '</td>' +
      '</tr>';
    (b.scope === 'staff' ? staffRows : storeRows).push(row);
  });
  var head = '<tr style="text-align:start;color:var(--sub,#5e6863);font-size:11px;text-transform:uppercase;letter-spacing:.04em"><th style="padding:6px 10px">Badge</th><th style="padding:6px 10px">When</th><th style="padding:6px 10px">Given for</th></tr>';
  return '<div id="achCatalogue" style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:6px 4px 12px;margin-bottom:16px;overflow-x:auto">' +
    '<div style="font-weight:800;font-size:13px;padding:8px 10px 2px">🏪 Store badges</div>' +
    '<table style="border-collapse:collapse;width:100%">' + head + storeRows.join('') + '</table>' +
    '<div style="font-weight:800;font-size:13px;padding:12px 10px 2px">👤 Individual badges</div>' +
    '<table style="border-collapse:collapse;width:100%">' + head + staffRows.join('') + '</table>' +
    '</div>';
}

function toggleCatalogue() { _showCat = !_showCat; paint(); }

function root() { return document.getElementById('achievementsAdminRoot'); }

async function render() {
  var el = root(); if (!el) return;
  el.innerHTML = '<div style="color:var(--sub,#5e6863);padding:20px">Loading achievements…</div>';
  try {
    var [allRes, cfgRes] = await Promise.all([
      api('/api/achievements/all'),
      api('/api/achievements/config'),
    ]);
    var all = allRes.ok ? await allRes.json() : null;
    var cfg = cfgRes.ok ? await cfgRes.json() : null;
    _awards = (all && all.awards) || [];
    _announce = !!(cfg && cfg.announce);
  } catch (e) { _awards = []; }
  paint();
}

function paint() {
  var el = root(); if (!el) return;
  var pending = (_awards || []).filter(function (a) { return !a.announced_at; });
  var q = _filter.toLowerCase();
  var shown = (_awards || []).filter(function (a) {
    if (!q) return true;
    return String(a.name).toLowerCase().indexOf(q) >= 0 ||
      String(a.store || '').toLowerCase().indexOf(q) >= 0 ||
      String(a.staff_name || '').toLowerCase().indexOf(q) >= 0 ||
      String(a.period).toLowerCase().indexOf(q) >= 0;
  });
  el.innerHTML =
    // ── how it works (v126.17 — Matt: nothing in admin explained the system) ──
    howItWorksHtml() +
    // ── controls row ──
    '<div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center;margin-bottom:14px">' +
      '<input id="achFilter" placeholder="🔍 Filter by badge / store / person / period" value="' + esc(_filter) + '" oninput="AchievementsAdmin.setFilter(this.value)" style="flex:1;min-width:220px;border:1.5px solid var(--border,#dde3e8);border-radius:10px;padding:9px 12px;font-size:13px;font-family:inherit">' +
      '<button class="btn btn-green" onclick="AchievementsAdmin.openStar()">⭐ Award a star</button>' +
      '<button class="btn btn-outline" onclick="AchievementsAdmin.openStar(\'heart\')" title="For a staffer a customer praised — the weekly auto-award also does this; use this for a typo it missed">💙 Award a Heart</button>' +
      '<button class="btn btn-outline" onclick="AchievementsAdmin.toggleCatalogue()">' + (_showCat ? '📖 Hide the catalogue' : '📖 What badges are given for') + '</button>' +
      '<button class="btn btn-outline" onclick="AchievementsAdmin.backfill(this)">⏪ Back-compute history</button>' +
      '<button class="btn btn-outline" onclick="AchievementsAdmin.sweepNow(this)" title="Runs exactly what the 3am cron runs, including the 💙 heart step — any failing step shows its error here">🔁 Run nightly sweep now (takes ~10 min)</button>' +
      '<button class="btn btn-outline" onclick="AchievementsAdmin.openPhotos()">📷 Profile photos</button>' +
    '</div>' +
    // ── the catalogue (from the award engine itself — can never drift) ──
    (_showCat ? catalogueHtml() : '') +
    // ── the announce switch + preview (safe-auto-content: default OFF) ──
    '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:14px;margin-bottom:16px">' +
      '<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">' +
        '<div style="font-weight:800;font-size:14px">📣 Announcements</div>' +
        '<div style="font-size:12px;color:var(--sub,#5e6863);flex:1;min-width:200px">When ON, a NEW badge fires a news post + a chat message to the winning store + a push. History and past awards never announce.</div>' +
        '<button onclick="AchievementsAdmin.toggleAnnounce(this)" style="border:none;border-radius:20px;padding:8px 18px;font-weight:800;font-size:13px;cursor:pointer;font-family:inherit;color:#fff;background:' + (_announce ? 'var(--elc-green,#009E3D)' : '#8a94a6') + '">' + (_announce ? 'ON — announcing' : 'OFF — silent') + '</button>' +
      '</div>' +
      (pending.length
        ? '<div style="margin-top:10px;font-size:12px;color:var(--adm-comms-gold-text);background:var(--adm-comms-warn-soft);border-radius:10px;padding:8px 12px">' + pending.length +
          (_announce ? ' award(s) announcing shortly (new ⭐ stars fire instantly; the rest go out with the nightly run): ' : ' award(s) would announce when switched on: ') +
          '<span translate="no">' + esc(pending.slice(0, 5).map(function (a) { return a.emoji + ' ' + a.name + ' → ' + (a.staff_name || a.store || ''); }).join(' · ')) + (pending.length > 5 ? ' …' : '') + '</span></div>'
        : '<div style="margin-top:10px;font-size:12px;color:var(--sub,#5e6863)">Nothing pending — every award so far is marked announced/silent.</div>') +
    '</div>' +
    // ── the grid ──
    '<div style="font-weight:800;font-size:14px;margin:4px 0 10px">🏆 All awards <span style="color:var(--sub,#5e6863);font-weight:600;font-size:12px">(' + shown.length + ')</span></div>' +
    (shown.length
      ? '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:10px">' +
        shown.slice(0, 120).map(function (a) {
          return '<div style="background:var(--card);border:1px solid var(--border,#dde3e8);border-radius:14px;padding:10px 12px;display:flex;gap:10px;align-items:center">' +
            art(a, 46) +
            '<div style="min-width:0;flex:1"><div style="font-weight:800;font-size:13px;line-height:1.2">' + esc(a.name) + '</div>' +
            '<div translate="no" style="font-size:11.5px;color:var(--sub,#5e6863);margin-top:2px">' + esc(a.staff_name || a.store || '') + ' · ' + esc(a.period) + (a.value != null ? ' · ' + esc(a.value) : '') + '</div>' +
            (a.reason ? '<div translate="no" style="font-size:11px;color:var(--ui-warn);margin-top:2px">&ldquo;' + esc(a.reason) + '&rdquo;</div>' : '') +
            '</div></div>';
        }).join('') + '</div>'
      : '<div style="color:var(--sub,#5e6863);padding:20px;text-align:center">No awards yet — run ⏪ Back-compute history (after the v161 migration), or wait for the nightly sweep.</div>');
}

function setFilter(v) { _filter = String(v || ''); paint(); var i = document.getElementById('achFilter'); if (i) { i.focus(); try { i.setSelectionRange(i.value.length, i.value.length); } catch (e) {} } }

// ── ⭐ Award a star ──────────────────────────────────────────────────────────
// v126.20 (Matt: "the search function to award a star doesnt work well. want
// to search a name and it comes up") — the two dropdowns became ONE search
// box: type any part of a name or store, tap the match. _starChoices carries
// the current results so onclick handlers pass a plain index (no quoting).
var _starTarget = null;    // { kind:'store', store } | { kind:'staff', id, label }
var _starChoices = [];
var _starMode = 'star';    // 'star' (any target) | 'heart' (💙 a person, no cap)

function starCandidates() {
  var out = [];
  // 💙 hearts are for a PERSON only — no stores in heart mode.
  if (_starMode !== 'heart') try {
    ((typeof ALL_STORES !== 'undefined' && ALL_STORES) || []).forEach(function (s) {
      var name = s.name || s;
      out.push({ kind: 'store', store: name, label: '🏪 ' + name, search: String(name).toLowerCase() });
    });
  } catch (e) {}
  try {
    ((typeof staffData !== 'undefined' && staffData) || []).forEach(function (p) {
      if (p.active === false) return;
      var label = '👤 ' + p.name + ' · ' + (p.store || '');
      out.push({ kind: 'staff', id: p.id, label: label, search: (p.name + ' ' + (p.store || '')).toLowerCase() });
    });
  } catch (e) {}
  return out;
}

function filterStarTargets() {
  var box = document.getElementById('achStarResults');
  if (!box) return;
  var q = String((document.getElementById('achStarSearch') || {}).value || '').trim().toLowerCase();
  if (!q) { _starChoices = []; box.innerHTML = '<div style="padding:8px;color:var(--sub,#5e6863);font-size:12px">Start typing a person or store name…</div>'; return; }
  _starChoices = starCandidates().filter(function (c) { return c.search.indexOf(q) !== -1; }).slice(0, 12);
  box.innerHTML = _starChoices.length
    ? _starChoices.map(function (c, i) {
        return '<div translate="no" onclick="AchievementsAdmin.pickStarChoice(' + i + ')" style="padding:9px 10px;border-bottom:1px solid var(--border);cursor:pointer;font-size:13px;font-weight:700">' + esc(c.label) + '</div>';
      }).join('')
    : '<div style="padding:8px;color:var(--sub,#5e6863);font-size:12px">No matches for &ldquo;' + esc(q) + '&rdquo;</div>';
}

function pickStarChoice(i) {
  var c = _starChoices[i];
  if (!c) return;
  _starTarget = c;
  var chip = document.getElementById('achStarPicked');
  if (chip) chip.innerHTML = '<span translate="no" style="display:inline-flex;align-items:center;gap:8px;background:var(--elc-green-soft);border:1.5px solid var(--elc-green,#009E3D);border-radius:10px;padding:7px 12px;font-size:13px;font-weight:800">' + esc(c.label) +
    ' <span onclick="AchievementsAdmin.clearStarTarget()" style="cursor:pointer;color:var(--adm-comms-red-text);font-weight:900">✕</span></span>';
  var box = document.getElementById('achStarResults'); if (box) box.innerHTML = '';
  var input = document.getElementById('achStarSearch'); if (input) input.value = '';
  var reason = document.getElementById('achStarReason'); if (reason) reason.focus();
}

function clearStarTarget() {
  _starTarget = null;
  var chip = document.getElementById('achStarPicked'); if (chip) chip.innerHTML = '';
  var input = document.getElementById('achStarSearch'); if (input) input.focus();
}

function openStar(mode) {
  _starMode = (mode === 'heart') ? 'heart' : 'star';
  var heart = _starMode === 'heart';
  var old = document.getElementById('achStarModal'); if (old) old.remove();
  _starTarget = null; _starChoices = [];
  var wrap = document.createElement('div');
  wrap.id = 'achStarModal';
  wrap.style.cssText = 'position:fixed;inset:0;background:var(--overlay);z-index:9000;display:flex;align-items:center;justify-content:center;padding:16px';
  wrap.innerHTML = '<div style="background:var(--card);border-radius:16px;max-width:440px;width:100%;padding:18px">' +
    '<div style="font-weight:900;font-size:16px">' + (heart ? '💙 Award a Customer Service Heart' : '⭐ Award a Star Badge') + '</div>' +
    '<div style="font-size:12px;color:var(--sub,#5e6863);margin:4px 0 12px">' + (heart ? 'For a staff member a customer praised. The weekly auto-award already does this from customer feedback — use this when a customer mis-spelt the name and it was missed. Spotlights 7 days.' : 'Ad hoc, for anything — it spotlights for 7 days with your reason. Max 3 per week so they stay special.') + '</div>' +
    '<label style="font-size:12px;font-weight:700">' + (heart ? 'Which staff member?' : 'Who is it for?') + '</label>' +
    '<input id="achStarSearch" type="text" placeholder="🔍 Type a person or store name…" oninput="AchievementsAdmin.filterStarTargets()" style="width:100%;box-sizing:border-box;border:1.5px solid var(--border,#dde3e8);border-radius:10px;padding:9px;font-size:13px;font-family:inherit;margin:4px 0 4px">' +
    '<div id="achStarResults" style="max-height:180px;overflow-y:auto;border-radius:10px"></div>' +
    '<div id="achStarPicked" style="margin:6px 0 10px;min-height:0"></div>' +
    '<label style="font-size:12px;font-weight:700">Why (shows on the badge)</label>' +
    '<input id="achStarReason" maxlength="200" placeholder="e.g. Covered three shifts at zero notice" style="width:100%;box-sizing:border-box;border:1.5px solid var(--border,#dde3e8);border-radius:10px;padding:9px;font-size:13px;font-family:inherit;margin:4px 0 4px">' +
    '<div id="achStarErr" style="color:var(--adm-comms-bad-text);font-size:12px;min-height:16px"></div>' +
    '<div style="display:flex;gap:8px;margin-top:8px">' +
      '<button class="btn btn-green" style="flex:1" onclick="AchievementsAdmin.sendStar(this)">Award ' + (heart ? '💙' : '⭐') + '</button>' +
      '<button class="btn btn-outline" onclick="document.getElementById(\'achStarModal\').remove()">Cancel</button></div></div>';
  wrap.addEventListener('click', function (e) { if (e.target === wrap) wrap.remove(); });
  document.body.appendChild(wrap);
  var input = document.getElementById('achStarSearch'); if (input) input.focus();
  filterStarTargets();
}

async function sendStar(btn) {
  var heart = _starMode === 'heart';
  var reason = ((document.getElementById('achStarReason') || {}).value || '').trim();
  var errEl = document.getElementById('achStarErr');
  if (!_starTarget) { errEl.textContent = heart ? 'Search and tap a staff member first.' : 'Search and tap a person or store first.'; return; }
  if (!heart && reason.length < 3) { errEl.textContent = 'Give a short reason — it shows on the badge.'; return; }
  btn.disabled = true;
  try {
    var body = heart
      ? { badge: 'customer-heart', staff_id: Number(_starTarget.id), reason: reason }
      : (_starTarget.kind === 'staff' ? { staff_id: Number(_starTarget.id), reason: reason } : { store: _starTarget.store, reason: reason });
    var res = await api('/api/achievements/star', { method: 'POST', body: JSON.stringify(body) });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok) { errEl.textContent = (d && d.error) || ('Could not award the ' + (heart ? 'heart' : 'star')); btn.disabled = false; return; }
    var m = document.getElementById('achStarModal'); if (m) m.remove();
    if (typeof showToast === 'function') showToast((heart ? '💙 Heart' : '⭐ Star') + ' awarded — it spotlights for 7 days');
    render();
  } catch (e) { errEl.textContent = 'Could not award — check the connection'; btn.disabled = false; }
}

// ── The switch + backfill ────────────────────────────────────────────────────
async function toggleAnnounce(btn) {
  btn.disabled = true;
  try {
    var res = await api('/api/achievements/config', { method: 'POST', body: JSON.stringify({ announce: !_announce }) });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok) { _announce = !!d.announce; paint(); if (typeof showToast === 'function') showToast(_announce ? '📣 Announcements ON — new badges will post' : '🔕 Announcements OFF'); }
    else if (typeof showToast === 'function') showToast((d && d.error) || 'Could not change the switch', 'error');
  } catch (e) { if (typeof showToast === 'function') showToast('Could not change the switch — check the connection', 'error'); }
  btn.disabled = false;
}

async function backfill(btn) {
  btn.disabled = true; btn.textContent = '⏳ Computing history…';
  try {
    var res = await api('/api/achievements/backfill', { method: 'POST', body: JSON.stringify({}) });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok) { if (typeof showToast === 'function') showToast('🏆 History computed: ' + (d.written || 0) + ' new award(s), ' + (d.skipped || 0) + ' already there'); render(); return; }
    if (typeof showToast === 'function') showToast((d && d.error) || 'Backfill failed — is v161 run on this environment?', 'error');
  } catch (e) { if (typeof showToast === 'function') showToast('Backfill failed — check the connection', 'error'); }
  btn.disabled = false; btn.textContent = '⏪ Back-compute history';
}

// v127.15 (hearts debugging): run the REAL nightly sweep on demand — unlike the
// backfill this includes the 💙 heart AI step (same gates as the 3am cron; one
// model call per week ever). Errors are shown VERBATIM, because for weeks they
// died invisibly in the Cloudflare console while hearts never landed.
async function sweepNow(btn) {
  btn.disabled = true; btn.textContent = '⏳ Running the nightly sweep…';
  try {
    var res = await api('/api/achievements/sweep-now', { method: 'POST', body: JSON.stringify({}) });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok) {
      var msg = '🔁 Sweep done: ' + (d.written || 0) + ' new award(s), ' + (d.skipped || 0) + ' already there' +
        (typeof d.hearts === 'number' ? ', 💙 ' + d.hearts + ' heart(s)' : '');
      if (d.errors && d.errors.length) {
        if (typeof showToast === 'function') showToast(msg + ' — but ' + d.errors.length + ' step(s) FAILED', 'error');
        window.alert('Sweep step errors (this is the hearts diagnosis):\n\n' + d.errors.join('\n\n'));
      } else if (typeof showToast === 'function') showToast(msg);
      render(); return;
    }
    if (typeof showToast === 'function') showToast((d && d.error) || 'Sweep failed', 'error');
  } catch (e) { if (typeof showToast === 'function') showToast('Sweep failed — check the connection', 'error'); }
  btn.disabled = false; btn.textContent = '🔁 Run nightly sweep now';
}

// ── 📷 the profile-photo oversight grid (v126.7, spec Part 3) ────────────────
// Newest first; one-tap remove. This is the human filter behind "immediate
// display + oversight" — no pre-approval queue, but nothing lingers unseen.
function okAvatarImg(s) {
  return /^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/.test(String(s || '')) ? String(s) : '';
}
async function openPhotos() {
  var old = document.getElementById('achPhotosModal'); if (old) old.remove();
  var wrap = document.createElement('div');
  wrap.id = 'achPhotosModal';
  wrap.style.cssText = 'position:fixed;inset:0;background:var(--overlay);z-index:9000;display:flex;align-items:center;justify-content:center;padding:16px';
  wrap.innerHTML = '<div style="background:var(--card);border-radius:16px;max-width:720px;width:100%;padding:18px;max-height:88vh;overflow-y:auto">' +
    '<div style="display:flex;align-items:center;gap:8px"><div style="font-weight:900;font-size:16px">📷 Profile photos</div>' +
    '<div style="font-size:11.5px;color:var(--sub,#5e6863);flex:1">newest first — remove anything that is not a clear headshot</div>' +
    '<button onclick="document.getElementById(\'achPhotosModal\').remove()" style="border:none;background:none;font-size:18px;cursor:pointer;color:var(--tiny)">✕</button></div>' +
    '<div id="achPhotosGrid" style="margin-top:12px;color:var(--sub,#5e6863)">Loading…</div></div>';
  wrap.addEventListener('click', function (e) { if (e.target === wrap) wrap.remove(); });
  document.body.appendChild(wrap);
  try {
    var res = await api('/api/avatar/all');
    var d = res.ok ? await res.json() : null;
    var grid = document.getElementById('achPhotosGrid');
    if (!grid) return;
    var photos = (d && d.photos) || [];
    if (!photos.length) { grid.innerHTML = 'No photos yet — staff add theirs from the chat name picker.'; return; }
    grid.innerHTML = '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px">' +
      photos.map(function (p) {
        var img = okAvatarImg(p.photo);
        var target = p.staff_id ? "{staff_id:" + p.staff_id + "}" : "{account_id:" + p.account_id + "}";
        return '<div style="border:1px solid var(--border,#dde3e8);border-radius:12px;padding:8px;text-align:center">' +
          (img ? '<img src="' + img + '" style="width:84px;height:84px;border-radius:42px;object-fit:cover">' : '') +
          '<div translate="no" style="font-size:11px;font-weight:700;margin-top:4px;line-height:1.2">' + esc(p.who) + '</div>' +
          '<button onclick=\'AchievementsAdmin.removePhoto(' + target + ', this)\' style="margin-top:6px;border:none;background:var(--adm-comms-danger-soft);color:var(--adm-comms-bad-text);border-radius:8px;padding:4px 10px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit">Remove</button></div>';
      }).join('') + '</div>';
  } catch (e) {
    var g = document.getElementById('achPhotosGrid');
    if (g) g.textContent = 'Could not load photos — check the connection.';
  }
}
async function removePhoto(target, btn) {
  btn.disabled = true;
  try {
    var res = await api('/api/avatar/remove', { method: 'POST', body: JSON.stringify(target) });
    if (res.ok) {
      if (typeof showToast === 'function') showToast('Photo removed — they can retake it any time');
      openPhotos();                                     // repaint the grid
      return;
    }
  } catch (e) {}
  btn.disabled = false;
  if (typeof showToast === 'function') showToast('Could not remove the photo', 'error');
}

export { render, paint, setFilter, openStar, sendStar, toggleAnnounce, backfill, sweepNow, openPhotos, removePhoto, toggleCatalogue, filterStarTargets, pickStarChoice, clearStarTarget };
